import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { useAuth } from '@/middlewares/auth/auth-context'
import { useCreateEvent } from '@/contexts/create-event-context'
import ScoringHorizontalSVG from '@/images/scoring-horizontal.svg?react'
import FacebookSVG from '@/images/svgs/Facebook.svg?react'
import InstagramSVG from '@/images/svgs/Instagram.svg?react'
import YoutubeSVG from '@/images/svgs/Youtube.svg?react'

const LINKS = [
    { label: 'Our Services', href: 'https://info.scoring.fit/' },
    { label: 'Help & FAQ', href: 'https://help.scoring.fit/' },
    // Opens the create-event modal in place (mock [data-create]); see onAction below.
    { label: 'Create an Event', action: 'create' },
]

const PROFILE = [
    { label: 'My Account', to: '/my-account' },
    { label: 'My Events', to: '/my-events' },
    { label: 'My Competitions', to: '/my-competitions' },
]

// TODO: replace '#' with the real Scoring.fit social URLs.
const SOCIALS = [
    { label: 'Facebook', href: '#', Icon: FacebookSVG },
    { label: 'Instagram', href: '#', Icon: InstagramSVG },
    { label: 'Youtube', href: '#', Icon: YoutubeSVG },
]

const LEGAL = [
    { label: 'Legal notice', href: 'https://info.scoring.fit/legalnotice' },
    { label: 'Terms of use', href: 'https://info.scoring.fit/termsofuse' },
    { label: 'Privacy Information', href: 'https://info.scoring.fit/privacypolicy' },
    // Reopens the cookie preferences panel — the one entry point a logged-out visitor has.
    { label: 'Cookies', action: 'cookies' },
]

// Kit .ftr-col a: 12px, 24px min row, 8px icon gap, muted → white on hover.
const linkClass = 'inline-flex min-h-6 items-center gap-2 text-xs text-white/50 transition-colors hover:text-white'
// Kit .ftr-legal a: 11px, underlined.
const legalLinkClass = 'text-[11px] text-white/50 underline transition-colors hover:text-white'

const Divider = ({ className = '' }) => <hr className={`m-0 w-full border-0 border-t border-white/15 ${className}`} />

function FooterLink({ item, t, onAction, className = linkClass }) {
    const content = (
        <>
            {item.Icon && <item.Icon className="size-3.5 shrink-0" aria-hidden />}
            {t(item.label)}
        </>
    )
    if (item.action) {
        return (
            <button
                type="button"
                onClick={() => onAction(item.action)}
                className={`${className} cursor-pointer border-0 bg-transparent p-0 [font:inherit]`}
            >
                {content}
            </button>
        )
    }
    return item.to ? (
        <Link to={item.to} className={className}>
            {content}
        </Link>
    ) : (
        <a href={item.href} target="_blank" rel="noreferrer" className={className}>
            {content}
        </a>
    )
}

/** A titled link column (kit .ftr-col): 14px bold title in the body font, 12px rows 8px apart. */
function FooterColumn({ title, items, t, onAction }) {
    return (
        <div className="flex flex-col gap-3">
            <h3 className="m-0 text-sm font-bold text-white">{t(title)}</h3>
            <div className="flex flex-col gap-2">
                {items.map((item) => (
                    <FooterLink key={item.label} item={item} t={t} onAction={onAction} />
                ))}
            </div>
        </div>
    )
}

/**
 * Site footer (kit .ftr — the one inverted surface). Full-bleed dark bar, content inset
 * like the navbar (var(--x006)) on desktop; 32/16/20 padding under the kit's 760px
 * `navbar` breakpoint. One layout at every width: brand (logo + tagline), then the
 * Links · Profile · Follow Us columns — a row on desktop, a plain column on mobile —
 * and a divided bottom bar (copyright + legal links).
 */
export default function Footer() {
    // homePage carries the footer strings; the link labels shared with the navbar
    // (Our Services, My Events…) resolve from the navbar namespace.
    const { t } = useTranslation([TranslationNS.HOME_PAGE, TranslationNS.NAVBAR])
    const authContext = useAuth()
    const { open: openCreateEvent } = useCreateEvent()
    // 'prefs' opens the cookie card straight on its preferences panel (same call as My Account).
    // 'create' opens the modal over the page; logged out, the context routes the visitor through
    // the login to My Events, which opens it on arrival (see create-event-context).
    const onAction = (action) => {
        if (action === 'cookies') authContext.setCookiesConsentOpen('prefs')
        if (action === 'create') openCreateEvent()
    }

    return (
        <footer className="mt-(--x009) flex flex-col gap-8 bg-(--footer-bg) px-4 pt-8 pb-5 text-white nav:px-(--x006) nav:pt-12 nav:pb-6">
            <div className="flex flex-col gap-7 nav:flex-row nav:flex-wrap nav:justify-between nav:gap-12">
                <div className="flex max-w-90 flex-col items-start gap-3">
                    <ScoringHorizontalSVG className="h-8 w-auto [&_path]:fill-white" />
                    <p className="m-0 text-xs leading-[1.6] text-white/50">
                        {t(
                            'Scoring.fit is the all in one solution to manage your fitness competition, easy to use, quickly create physical or online events for either 100 or 100.000 athletes !',
                        )}
                    </p>
                </div>

                <div className="flex flex-col gap-7 nav:flex-row nav:flex-wrap nav:gap-12">
                    <FooterColumn title="Links" items={LINKS} t={t} onAction={onAction} />
                    <FooterColumn title="Profile" items={PROFILE} t={t} />
                    <FooterColumn title="Follow Us" items={SOCIALS} t={t} />
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <Divider />
                <div className="flex flex-col gap-4 text-[11px] text-white/50 nav:flex-row nav:flex-wrap nav:items-center nav:justify-between">
                    <p className="m-0">
                        © {new Date().getFullYear()} - INFO SCORING.FIT | {t('All rights reserved')}
                    </p>
                    <div className="flex flex-wrap gap-4">
                        {LEGAL.map((item) => (
                            <FooterLink
                                key={item.label}
                                item={item}
                                t={t}
                                onAction={onAction}
                                className={legalLinkClass}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    )
}
